import { BuiltOnRadium } from '@/components/content/BuiltOnRadium';
import { ClosingCTA } from '@/components/content/ClosingCTA';
import { ElementCard } from '@/components/content/ElementCard';
import { Eyebrow } from '@/components/content/Eyebrow';
import { MoatBlock } from '@/components/content/MoatBlock';
import { PersonalizationCompareTable } from '@/components/content/PersonalizationCompareTable';
import { Button } from '@/components/interactive/Button';
import { AnchorNav } from '@/components/nav/AnchorNav';
import { Reveal } from '@/components/utils/RevealOnScroll';
import { SEO } from '@/components/utils/SEO';
import { ChainVisual } from '@/components/visuals/ChainVisual';
import { OrbitDiagram } from '@/components/visuals/OrbitDiagram';
import {
  jtbdPillars,
  partnershipPhases,
  personalizationHero,
} from '@/content/platform/personalization';

const productSections = [
  { id: 'intelligence', label: 'Intelligence' },
  { id: 'decisioning', label: 'Decisioning' },
  { id: 'generative', label: 'Generative' },
  { id: 'stack-fit', label: 'Stack fit' },
];

/**
 * The moat — PRD.md §1.4 in enterprise voice.
 * Inserted between AnchorNav and the Intelligence section.
 */
const personalizationMoat = {
  eyebrow: 'What makes this hard to build yourself',
  title: 'Four places internal personalization projects stall.',
  lede: 'Every enterprise with a content surface and a data team has, at some point, considered building this internally. Many have started. Most stall in the same four places — and they are coordination problems, not modeling problems.',
  items: [
    {
      headline: 'CMS-to-warehouse-to-surface integration is genuinely hard',
      body: 'Content schemas drift. Taxonomies are inconsistent. The API contract between editorial tools and the runtime is rarely owned by one team. Radium reads enriched profiles from your data foundation and operates on owned surfaces — without asking either side to change.',
    },
    {
      headline: 'Tagging is a coordination problem, not a model problem',
      body: 'A model can suggest tags all day. The bottleneck is the editorial workflow, the approval surface, the audit trail, and the feedback loop back into the model. Radium ships the workflow with the model — editors approve in batches, every decision retrains.',
    },
    {
      headline: 'In-session ranking has to be fast, explainable, and compliant — at the same time',
      body: '"The model said so" is not an acceptable answer to a compliance review, especially in regulated verticals. Every Radium ranking decision returns the signals that drove it, logged with full provenance, reviewable in the admin.',
    },
    {
      headline: 'Closing the feedback loop in real time means owning the runtime',
      body: 'A reverse-ETL round-trip through the warehouse takes hours. Radium owns the impression, the click, and the dwell time, and updates ranking signals on the surface in seconds. The feedback loop is the runtime, not a downstream job.',
    },
  ],
};

function SubFeature({ title, body }: { title: string; body: string }) {
  return (
    <div
      className="glass glass--settled"
      style={{ padding: 28, height: '100%', borderRadius: 12 }}
    >
      <h4
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 18,
          fontWeight: 600,
          color: 'var(--ink-primary)',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'var(--accent)',
            boxShadow: '0 0 8px var(--radium-glow)',
            flexShrink: 0,
          }}
        />
        {title}
      </h4>
      <p
        style={{ color: 'var(--ink-secondary)', fontSize: 14, lineHeight: 1.6 }}
      >
        {body}
      </p>
    </div>
  );
}

function ProductProse({
  tag,
  h2,
  body,
  symbol,
  number,
}: {
  tag: string;
  h2: string;
  body: string;
  symbol: string;
  number: string;
}) {
  const labelMap: Record<string, string> = {
    Cf: 'Content',
    Dc: 'Decisioning',
    Gn: 'Generative',
    Ad: 'Admin',
    In: 'Integrations',
  };
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 24,
        }}
      >
        <ElementCard
          symbol={symbol}
          number={number}
          label={labelMap[symbol] || ''}
          size="md"
        />
        <div className="text-eyebrow" style={{ color: 'var(--accent)' }}>
          {tag}
        </div>
      </div>
      <h2 className="text-h2">{h2}</h2>
      <p className="text-body-lg" style={{ marginTop: 24 }}>
        {body}
      </p>
    </div>
  );
}

interface ProductSectionProps {
  id: string;
  symbol: string;
  number: string;
  tag: string;
  h2: string;
  body: string;
  visual: React.ReactNode;
  features: { title: string; body: string }[];
  reverse?: boolean;
}

function ProductSection({
  id,
  symbol,
  number,
  tag,
  h2,
  body,
  visual,
  features,
  reverse,
}: ProductSectionProps) {
  return (
    <section
      id={id}
      className="section"
      style={{
        borderTop: '1px solid var(--border-subtle)',
        scrollMarginTop: 120,
      }}
    >
      <div className="container">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: reverse ? '1.2fr 1fr' : '1fr 1.2fr',
            gap: 64,
            alignItems: 'start',
          }}
          className="prod-grid"
        >
          {!reverse && (
            <Reveal>
              <ProductProse
                tag={tag}
                h2={h2}
                body={body}
                symbol={symbol}
                number={number}
              />
            </Reveal>
          )}
          <Reveal delay={120}>
            <div
              className="glass glass--settled"
              style={{ padding: 28, borderRadius: 16, minHeight: 400, overflow: 'hidden' }}
            >
              {visual}
            </div>
          </Reveal>
          {reverse && (
            <Reveal>
              <ProductProse
                tag={tag}
                h2={h2}
                body={body}
                symbol={symbol}
                number={number}
              />
            </Reveal>
          )}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginTop: 64,
          }}
          className="features-2x2"
        >
          {features.map((f, i) => (
            <Reveal key={f.title} delay={i * 60}>
              <SubFeature {...f} />
            </Reveal>
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 880px) {
          .prod-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .features-2x2 { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

function IntelligenceVisual() {
  const items = [
    {
      title: 'How to read a 10-K',
      tags: ['fundamentals', 'investing', 'education'],
      status: 'approved',
    },
    {
      title: 'Compound interest, visualized',
      tags: ['math', 'savings', 'calculator'],
      status: 'approved',
    },
    {
      title: '401(k) rollovers in 4 steps',
      tags: ['retirement', 'process'],
      status: 'pending',
    },
    {
      title: 'When index funds beat active',
      tags: ['investing', 'research'],
      status: 'approved',
    },
  ];
  return (
    <div>
      <div className="text-eyebrow" style={{ marginBottom: 16 }}>
        CONTENT GRAPH · LIVE
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((it) => (
          <div
            key={it.title}
            className="glass-chip"
            style={{
              padding: '12px 14px',
              borderRadius: 8,
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 12,
              alignItems: 'center',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'var(--ink-primary)',
                  marginBottom: 6,
                }}
              >
                {it.title}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {it.tags.map((t) => (
                  <span
                    key={t}
                    className="text-mono glass-chip"
                    style={{
                      fontSize: 11,
                      padding: '3px 8px',
                      borderRadius: 4,
                      color: 'var(--ink-secondary)',
                    }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div
              className="text-mono"
              style={{
                fontSize: 11,
                padding: '3px 8px',
                borderRadius: 3,
                background:
                  it.status === 'approved'
                    ? 'rgba(0,218,165,0.12)'
                    : 'rgba(198,138,15,0.12)',
                color:
                  it.status === 'approved' ? 'var(--accent)' : 'var(--warning)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {it.status}
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          color: 'var(--ink-muted)',
          fontSize: 12,
          fontFamily: 'var(--font-mono)',
        }}
      >
        <span>4 / 1,247 items</span>
        <span style={{ color: 'var(--accent)' }}>● tag coverage 84%</span>
      </div>
    </div>
  );
}

function DecisioningVisual() {
  return (
    <div>
      <div className="text-eyebrow" style={{ marginBottom: 16 }}>
        RANK RESPONSE · session #a31f...
      </div>
      <div style={{ marginBottom: 16 }}>
        <ChainVisual />
      </div>
      <div
        className="glass glass--settled"
        style={{ borderRadius: 10, overflow: 'hidden' }}
      >
        <div
          style={{
            padding: 16,
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            lineHeight: 1.7,
            background: 'var(--gray-950)',
          }}
        >
          <div style={{ color: 'var(--ink-muted)' }}>
            {'// p95 latency: 142ms · explainable'}
          </div>
          <div style={{ color: 'var(--ink-secondary)' }}>
            <span style={{ color: 'var(--accent)' }}>rank</span>(profile=u_8821,
            surface=feed) {'=>'}
          </div>
          <div style={{ paddingLeft: 16, color: 'var(--ink-secondary)' }}>
            [<span style={{ color: 'var(--accent)' }}>0.91</span>] item:c_4029 ·
            signals: dwell↑, similar:0.83
          </div>
          <div style={{ paddingLeft: 16, color: 'var(--ink-secondary)' }}>
            [<span style={{ color: 'var(--accent)' }}>0.87</span>] item:c_1184 ·
            signals: persona:retiree, recency
          </div>
          <div style={{ paddingLeft: 16, color: 'var(--ink-secondary)' }}>
            [<span style={{ color: 'var(--accent)' }}>0.84</span>] item:c_7733 ·
            signals: cohort:saver, embedding↑
          </div>
          <div style={{ paddingLeft: 16, color: 'var(--ink-muted)' }}>
            [0.71] item:c_2093 · signals: editorial:elevated
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminVisual() {
  const items = [
    {
      title: 'The case for high-yield savings',
      tag: '+ savings · + emergency-fund',
      confidence: 0.94,
    },
    {
      title: 'What to do when your stock vests',
      tag: '+ equity · + tax · - retirement',
      confidence: 0.81,
    },
    {
      title: 'How CD ladders work',
      tag: '+ savings · + fixed-income',
      confidence: 0.88,
    },
  ];
  return (
    <div>
      <div className="text-eyebrow" style={{ marginBottom: 16 }}>
        EDITORIAL QUEUE · 23 PROPOSALS
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((it) => (
          <div
            key={it.title}
            className="glass-chip"
            style={{
              padding: 12,
              borderRadius: 8,
              display: 'grid',
              gridTemplateColumns: '1fr auto auto',
              gap: 12,
              alignItems: 'center',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'var(--ink-primary)',
                }}
              >
                {it.title}
              </div>
              <div
                className="text-mono"
                style={{
                  fontSize: 11,
                  color: 'var(--ink-muted)',
                  marginTop: 4,
                }}
              >
                {it.tag}
              </div>
            </div>
            <div
              className="text-mono"
              style={{ fontSize: 11, color: 'var(--accent)' }}
            >
              {it.confidence.toFixed(2)}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <span
                style={{
                  padding: '4px 8px',
                  fontSize: 11,
                  background: 'var(--accent)',
                  color: 'var(--ink-on-accent)',
                  borderRadius: 3,
                  fontWeight: 500,
                }}
              >
                ✓
              </span>
              <span
                style={{
                  padding: '4px 8px',
                  fontSize: 11,
                  background: 'transparent',
                  color: 'var(--ink-muted)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 3,
                }}
              >
                ✗
              </span>
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: 'var(--ink-muted)',
        }}
      >
        <span>BULK APPROVE · J/K · ⌘⏎</span>
        <span>EDITOR: a.morales · v2.3.1</span>
      </div>
    </div>
  );
}

const integrationSources = [
  { name: 'Hightouch', note: 'first-class · CDP / audience' },
  { name: 'Census', note: 'reverse-ETL / audience' },
  { name: 'RudderStack', note: 'warehouse-native CDP' },
  { name: 'Segment', note: 'events / traits' },
  { name: 'Snowflake', note: 'direct warehouse' },
  { name: 'Databricks', note: 'lakehouse / model artifacts' },
  { name: 'mParticle', note: 'events / identity' },
  { name: 'Tealium', note: 'events / identity' },
  { name: 'Contentful', note: 'first-class CMS' },
  { name: 'Sanity', note: 'headless CMS' },
  { name: 'Strapi', note: 'headless CMS' },
  { name: 'Custom', note: 'Radium ingestion API' },
];

function IntegrationsSection() {
  return (
    <section
      id="stack-fit"
      className="section"
      style={{
        borderTop: '1px solid var(--border-subtle)',
        scrollMarginTop: 120,
      }}
    >
      <div className="container">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.2fr',
            gap: 64,
            alignItems: 'start',
          }}
          className="prod-grid"
        >
          <Reveal>
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  marginBottom: 24,
                }}
              >
                <ElementCard
                  symbol="In"
                  number="24"
                  label="Integrations"
                  size="md"
                />
                <div
                  className="text-eyebrow"
                  style={{ color: 'var(--accent)' }}
                >
                  In / 24
                </div>
              </div>
              <h2 className="text-h2">
                Designed for the stack you've already built.
              </h2>
              <p className="text-body-lg" style={{ marginTop: 24 }}>
                Radium is not a CDP. It doesn't collect events, resolve
                identity, or sync to outbound destinations — those are jobs your
                existing stack already does well. Radium reads enriched
                profiles, audiences, and traits from your data foundation and
                operates on the surfaces your application owns. The integration
                is read-mostly, governed, and respects the boundary between data
                infrastructure and product runtime.
              </p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div
              className="glass glass--settled"
              style={{ padding: 24, borderRadius: 16 }}
            >
              <OrbitDiagram />
            </div>
          </Reveal>
        </div>

        <Reveal delay={240}>
          <div style={{ marginTop: 80 }}>
            <div className="text-eyebrow" style={{ marginBottom: 24 }}>
              Compatible with
            </div>
            <div
              className="glass glass--settled"
              style={{ padding: 8, borderRadius: 12 }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 8,
                }}
                className="logo-wall"
              >
                {integrationSources.map((s) => (
                  <div
                    key={s.name}
                    className="glass-chip"
                    style={{ padding: '20px 24px', borderRadius: 8 }}
                  >
                    <div
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontWeight: 600,
                        fontSize: 16,
                        color: 'var(--ink-primary)',
                      }}
                    >
                      {s.name}
                    </div>
                    <div
                      className="text-mono"
                      style={{
                        fontSize: 11,
                        color: 'var(--ink-muted)',
                        marginTop: 4,
                      }}
                    >
                      {s.note}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <p
              style={{ marginTop: 20, color: 'var(--ink-muted)', fontSize: 14 }}
            >
              If your stack produces governed, addressable user signals and your
              content library has an API, Radium can read it.
            </p>
          </div>
        </Reveal>
      </div>
      <style>{`@media (max-width: 880px) { .logo-wall { grid-template-columns: 1fr 1fr !important; } }`}</style>
    </section>
  );
}

function JtbdPillarOverview() {
  return (
    <section
      className="section"
      style={{ borderTop: '1px solid var(--border-subtle)' }}
    >
      <div className="container">
        <div
          className="jtbd-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 16,
          }}
        >
          {jtbdPillars.map((p, i) => (
            <Reveal key={p.symbol} delay={i * 80}>
              <a
                href={`#${p.anchor}`}
                className="glass glass--settled"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 20,
                  padding: 32,
                  borderRadius: 12,
                  textDecoration: 'none',
                  height: '100%',
                  transition: 'border-color 240ms var(--ease-out)',
                }}
              >
                <ElementCard
                  symbol={p.symbol}
                  number={p.number}
                  label={p.label}
                  size="md"
                />
                <h3
                  className="text-h3"
                  style={{ color: 'var(--ink-primary)' }}
                >
                  {p.title}
                </h3>
                <p
                  style={{
                    color: 'var(--ink-secondary)',
                    fontSize: 15,
                    lineHeight: 1.65,
                    margin: 0,
                  }}
                >
                  {p.body}
                </p>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 880px) {
          .jtbd-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

function GenerativeVisual() {
  const variants = [
    {
      surface: 'feed-card-001',
      headline: 'Three moves that flatten a market dip',
      cta: 'Read the breakdown',
      score: 0.91,
    },
    {
      surface: 'feed-card-001',
      headline: 'What to do when the market drops 10%',
      cta: 'Get the playbook',
      score: 0.86,
    },
    {
      surface: 'feed-card-001',
      headline: 'A 3-step plan for the next downturn',
      cta: 'See the steps',
      score: 0.79,
    },
  ];
  return (
    <div>
      <div className="text-eyebrow" style={{ marginBottom: 16 }}>
        VARIANT EVALUATION · session #c47e...
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {variants.map((v, i) => (
          <div
            key={`${v.surface}-${i}`}
            className="glass-chip"
            style={{
              padding: '12px 14px',
              borderRadius: 8,
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 12,
              alignItems: 'center',
              borderLeft:
                i === 0 ? '2px solid var(--accent)' : '2px solid transparent',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'var(--ink-primary)',
                  marginBottom: 4,
                }}
              >
                {v.headline}
              </div>
              <div
                className="text-mono"
                style={{
                  fontSize: 11,
                  color: 'var(--ink-muted)',
                }}
              >
                {v.cta}
              </div>
            </div>
            <div
              className="text-mono"
              style={{
                fontSize: 12,
                color: i === 0 ? 'var(--accent)' : 'var(--ink-muted)',
                fontWeight: 600,
              }}
            >
              {v.score.toFixed(2)}
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          color: 'var(--ink-muted)',
          fontSize: 12,
          fontFamily: 'var(--font-mono)',
        }}
      >
        <span>JTBD: avoid panic-selling · persona: cautious-saver</span>
        <span style={{ color: 'var(--accent)' }}>● variant.001 chosen</span>
      </div>
    </div>
  );
}

export function Personalization() {
  return (
    <>
      <SEO
        title="Radium Personalization"
        description="Generative recommendations on the surfaces you own. Aligned to Jobs-To-Be-Done, not just demographics. Content intelligence, JTBD decisioning, generative variants — built on the open-source Radium runtime."
        canonical="https://radium.love/personalization"
        ogImage="/og/og-personalization.png"
      />
      <section className="page-hero" style={{ paddingBottom: 64 }}>
        <div className="container">
          <Reveal>
            <Eyebrow
              symbol={personalizationHero.eyebrow.symbol}
              number={personalizationHero.eyebrow.number}
              label={personalizationHero.eyebrow.label}
            />
          </Reveal>
          <Reveal delay={80}>
            <h1 className="text-display" style={{ marginTop: 24, maxWidth: 1000 }}>
              {personalizationHero.title}
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p
              className="text-body-lg"
              style={{
                marginTop: 16,
                maxWidth: 800,
                color: 'var(--accent)',
                fontWeight: 500,
              }}
            >
              {personalizationHero.tagline}
            </p>
          </Reveal>
          <Reveal delay={220}>
            <p
              style={{
                marginTop: 16,
                maxWidth: 820,
                color: 'var(--ink-secondary)',
                fontSize: 17,
                lineHeight: 1.65,
              }}
            >
              {personalizationHero.description}
            </p>
          </Reveal>
          <Reveal delay={320}>
            <div
              style={{
                marginTop: 36,
                display: 'flex',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
              <Button href="/contact" variant="primary" size="lg" chevron>
                Contact us
              </Button>
              <Button href="/personalization/customers" variant="secondary" size="lg" chevron>
                See customer stories
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <BuiltOnRadium />

      <JtbdPillarOverview />

      <AnchorNav items={productSections} />

      <MoatBlock
        eyebrow={personalizationMoat.eyebrow}
        title={personalizationMoat.title}
        lede={personalizationMoat.lede}
        items={personalizationMoat.items}
      />

      <ProductSection
        id="intelligence"
        symbol="Cf"
        number="06"
        tag="Cf / 06"
        h2="Make your content library addressable."
        body="A personalization model is only as good as the content graph it operates on. Radium treats your content library as a first-class object — every article, video, card, calculator, and CTA gets tagged, embedded, and structured against a taxonomy you control. Tagging is suggested by a fine-tuned encoder model trained on your library, then approved by your editors. Embeddings update when content changes. The graph stays current without anyone babysitting it."
        visual={<IntelligenceVisual />}
        features={[
          {
            title: 'Tagging',
            body: 'Encoder models trained on your content propose tags. Editors approve, reject, or refine. The model learns from every decision.',
          },
          {
            title: 'Embeddings',
            body: 'Every content unit gets a vector representation that captures meaning, not just keywords. Similar content surfaces together. New content joins the graph automatically.',
          },
          {
            title: 'Taxonomy',
            body: 'You define the categories that matter to your business. Radium enforces them across the library and surfaces inconsistencies before they ship.',
          },
          {
            title: 'Content graph',
            body: "Pieces relate to each other through tags, embeddings, and editorial rules. Sequencing isn't random — it follows the graph.",
          },
        ]}
      />

      <ProductSection
        id="decisioning"
        symbol="Dc"
        number="12"
        tag="Dc / 12"
        h2="Ranks against the job — not just who they are."
        body="Outbound activation tools work in batches. Radium works in sessions, against Jobs-To-Be-Done. When a user opens your app, Radium reads their profile, infers the job they're hiring your product to do right now, queries the content graph, applies your business rules, and returns a ranked, sequenced response in under a second. When they engage — or don't — that signal feeds back into the ranking model immediately. No warehouse round-trip. No overnight job."
        visual={<DecisioningVisual />}
        reverse
        features={[
          {
            title: 'JTBD-aligned ranking',
            body: 'Decisioning reads the inferred job — not just demographics or cohort — and ranks micro-experiences against it. Editors define the jobs; the runtime aligns to them.',
          },
          {
            title: 'Sub-second ranking',
            body: 'P95 latency under 200ms for ranked feed queries. P99 under 400ms. Measured at the runtime, reported in the admin.',
          },
          {
            title: 'Real-time learning loop',
            body: "Impressions, clicks, and dwell time update ranking signals on the surface as they happen. The model you served at 9:00 isn't the model you serve at 9:15.",
          },
          {
            title: 'Explainability + compliance',
            body: 'Every ranking decision returns the signals that drove it — auditable per impression, reviewable in the admin, models private to your tenant, decisions logged with full provenance.',
          },
        ]}
      />

      <ProductSection
        id="generative"
        symbol="Gn"
        number="07"
        tag="Gn / 07"
        h2="Copy and modules generated in-session."
        body="Pre-authoring three variants of every headline doesn't scale, and A/B testing them takes weeks. Radium's generative layer produces copy, headlines, CTAs, and module compositions in real time — evaluated against the inferred job, scored against the content graph, and governed by editorial constraints. Editors approve the variant rules; the runtime generates and chooses."
        visual={<GenerativeVisual />}
        features={[
          {
            title: 'Generative copy variants',
            body: 'Headlines, summaries, CTAs, and microcopy generated in-session against the inferred job. Variants are evaluated, not pre-authored.',
          },
          {
            title: 'Module composition',
            body: 'Cards and modules assemble adaptively — a calculator paired with a primer for first-time users, a comparison view for evaluators, a deep-dive for return visitors.',
          },
          {
            title: 'Editorial governance over generation',
            body: 'Editors set constraints — voice, banned phrasings, required disclosures — and the runtime enforces them on every generated variant before it ships to a surface.',
          },
          {
            title: 'In-session evaluation',
            body: 'Variants are scored on engagement signals as they happen, with the strongest re-served and weakest retired without a deploy cycle.',
          },
        ]}
      />

      <section
        className="section"
        style={{ borderTop: '1px solid var(--border-subtle)' }}
      >
        <div className="container">
          <Reveal>
            <div
              className="surface"
              style={{
                padding: '32px 36px',
                borderRadius: 8,
                borderLeft: '3px solid var(--accent)',
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 32,
                alignItems: 'center',
              }}
            >
              <div>
                <div
                  className="text-eyebrow"
                  style={{ marginBottom: 12, color: 'var(--accent)' }}
                >
                  Editorial governance, throughout
                </div>
                <p
                  style={{
                    color: 'var(--ink-secondary)',
                    fontSize: 16,
                    lineHeight: 1.65,
                    maxWidth: 760,
                  }}
                >
                  Editors approve tag proposals, define jobs, set generative
                  constraints, retrain models on a schedule, and export audit
                  trails — all from one admin. Editorial keeps the final word
                  on every layer of the runtime.
                </p>
              </div>
              <ElementCard symbol="Ad" number="18" label="Admin" size="md" />
            </div>
          </Reveal>
        </div>
      </section>

      <IntegrationsSection />

      <section
        className="section"
        style={{ borderTop: '1px solid var(--border-subtle)' }}
      >
        <div className="container">
          <Reveal>
            <div className="text-eyebrow" style={{ color: 'var(--accent)' }}>
              How Radium compares
            </div>
            <h2 className="text-h2" style={{ marginTop: 12, maxWidth: 760 }}>
              Built for the post A/B test era.
            </h2>
            <p
              style={{
                marginTop: 16,
                color: 'var(--ink-secondary)',
                fontSize: 16,
                lineHeight: 1.6,
                maxWidth: 720,
                marginBottom: 40,
              }}
            >
              Optimization tools split traffic and wait for significance. Radium
              generates, scores, and serves in-session — with a CMS-native
              content graph and an open-source orchestration substrate
              underneath.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <PersonalizationCompareTable />
          </Reveal>
        </div>
      </section>

      <section
        className="section"
        style={{ borderTop: '1px solid var(--border-subtle)' }}
      >
        <div className="container">
          <Reveal>
            <div className="text-eyebrow" style={{ color: 'var(--accent)' }}>
              How it could work
            </div>
            <h2 className="text-h2" style={{ marginTop: 12, maxWidth: 760 }}>
              A partnership tailored to your stack.
            </h2>
            <p
              style={{
                marginTop: 16,
                color: 'var(--ink-secondary)',
                fontSize: 16,
                lineHeight: 1.6,
                maxWidth: 720,
                marginBottom: 40,
              }}
            >
              The Radium engagement scales from a focused pilot to a multi-surface
              enterprise deployment.
            </p>
          </Reveal>
          <div
            className="phases-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 16,
            }}
          >
            {partnershipPhases.map((phase, i) => (
              <Reveal key={phase.n} delay={i * 80}>
                <div
                  className="surface"
                  style={{ padding: 28, height: '100%', borderRadius: 8 }}
                >
                  <div
                    className="text-mono"
                    style={{
                      fontSize: 28,
                      fontWeight: 600,
                      color: 'var(--accent)',
                      marginBottom: 16,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {phase.n}
                  </div>
                  <h3 className="text-h4" style={{ marginBottom: 12 }}>
                    {phase.title}
                  </h3>
                  <p
                    style={{
                      color: 'var(--ink-secondary)',
                      fontSize: 14.5,
                      lineHeight: 1.6,
                    }}
                  >
                    {phase.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <style>{`
            @media (max-width: 880px) {
              .phases-grid { grid-template-columns: 1fr !important; }
            }
          `}</style>
        </div>
      </section>

      <ClosingCTA
        h2="Bring your stack. We'll bring the reaction."
        body="Custom-built around your stack, fully hosted, handed off working. If personalization is on the roadmap, we're the team that's done it before."
      />
    </>
  );
}
